import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";

/**
 * Default parameters to send with franchises requests
 */
const franchisesParamsToSend = {
  params: "id,name,internal_name,slug,logo,enabled,timezone",
};

const FRANCHISES_INFINITE_PAGE_SIZE = 10;

/**
 * Infinite-scroll franchises list (same API as useFranchises, paginated pages merged client-side)
 * @param {Object} options
 * @param {boolean} [options.enabled=true] - When false, no fetch (e.g. until dropdown or parent UI is ready)
 * @returns {Object} Infinite query plus flattened `franchises` array
 */
export const useFranchisesInfinite = ({ enabled = true } = {}) => {
  const query = useInfiniteQuery({
    queryKey: ["franchises", "infinite", FRANCHISES_INFINITE_PAGE_SIZE],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = {
        ...franchisesParamsToSend,
        page: pageParam,
        page_size: FRANCHISES_INFINITE_PAGE_SIZE,
      };
      const queryString = buildQueryParams(queryParams);
      const response = await apiClient.get(
        `/franchises${queryString ? `?${queryString}` : ""}`,
      );
      return {
        franchises: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      const currentPage = pagination.current_page || pagination.page || 1;
      const totalPages = pagination.total_pages || 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    enabled: !!enabled && isEndpointEnabled("franchises", "list"),
    staleTime: 5 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const franchises =
    query.data?.pages?.flatMap((page) => page.franchises) || [];

  return {
    ...query,
    franchises,
  };
};

/**
 * Hook to fetch franchises list with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 10)
 * @returns {Object} Query result with franchises and pagination
 */
export const useFranchises = (params = {}) => {
  const { page = 1, pageSize = 10 } = params;

  const queryParams = {
    ...franchisesParamsToSend,
    page,
    page_size: pageSize,
  };

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["franchises", page, pageSize],
    queryFn: async () => {
      const response = await apiClient.get(
        `/franchises${queryString ? `?${queryString}` : ""}`,
      );
      return {
        franchises: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("franchises", "list"),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to fetch a single franchise by ID
 * @param {number} franchiseId - Franchise ID
 * @returns {Object} Query result with franchise data
 */
export const useFranchise = (franchiseId) => {
  return useQuery({
    queryKey: ["franchise", franchiseId],
    queryFn: async () => {
      const response = await apiClient.get(`/franchises/${franchiseId}`);
      return response?.result || null;
    },
    enabled: !!franchiseId && isEndpointEnabled("franchises", "list"),
    staleTime: 5 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
};

/**
 * Hook to update a franchise
 * @returns {Object} Mutation object
 */
export const useUpdateFranchise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ franchiseId, data }) => {
      const response = await apiClient.post(`/franchises/${franchiseId}`, data);
      return response?.result;
    },
    onSuccess: (data, variables) => {
      const { franchiseId, data: updateData } = variables;

      // Optimistically update all franchises queries in the cache
      queryClient.setQueriesData({ queryKey: ["franchises"] }, (oldData) => {
        if (
          !oldData ||
          !oldData.franchises ||
          !Array.isArray(oldData.franchises)
        ) {
          return oldData;
        }

        return {
          ...oldData,
          franchises: oldData.franchises.map((franchise) =>
            franchise.id === franchiseId
              ? { ...franchise, ...updateData }
              : franchise,
          ),
        };
      });

      // Invalidate to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: ["franchises"] });
      queryClient.invalidateQueries({ queryKey: ["franchise", franchiseId] });
    },
  });
};

/**
 * Hook to create a new franchise
 * @returns {Object} Mutation object
 */
export const useCreateFranchise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.post("/franchises", data);
      return response?.result;
    },
    onSuccess: () => {
      // Invalidate franchises queries
      queryClient.invalidateQueries({ queryKey: ["franchises"] });
    },
  });
};

/**
 * Hook to delete a franchise
 * @returns {Object} Mutation object
 */
export const useDeleteFranchise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (franchiseId) => {
      const response = await apiClient.delete(`/franchises/${franchiseId}`);
      return response?.result;
    },
    onSuccess: () => {
      // Invalidate franchises queries
      queryClient.invalidateQueries({ queryKey: ["franchises"] });
    },
  });
};

/**
 * Hook to assign/unassign a business to a franchise
 * @returns {Object} Mutation object
 */
export const useAssignBusinessToFranchise = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ businessId, franchiseId }) => {
      const response = await apiClient.post(`/business/${businessId}`, {
        franchise_id: franchiseId ?? null,
      });
      return response?.result;
    },
    onSuccess: (_data, variables) => {
      const { businessId } = variables || {};
      // Invalidate affected caches
      queryClient.invalidateQueries({ queryKey: ["businesses"] });
      if (businessId) {
        queryClient.invalidateQueries({ queryKey: ["business", businessId] });
      }
    },
  });
};
